/**
 * Unassigned Meals Warning Modal
 * Shown when user tries to leave calendar with unscheduled recipes
 */

import React from 'react'
import { RecipeRecord } from '@talktoast/shared'

interface UnassignedMealsWarningProps {
  open: boolean
  unassignedRecipes: RecipeRecord[]
  assignedCount: number
  onStay: () => void
  onLeave: () => void
}

export const UnassignedMealsWarning: React.FC<UnassignedMealsWarningProps> = ({
  open,
  unassignedRecipes,
  assignedCount,
  onStay,
  onLeave
}) => {
  if (!open) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-fade-in">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="text-5xl mb-3">📋</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            You have {unassignedRecipes.length} unscheduled {unassignedRecipes.length === 1 ? 'meal' : 'meals'}
          </h2>
        </div>

        {/* Stats */}
        <div className="bg-blue-50 rounded-lg p-4 mb-6">
          <div className="flex justify-around text-center">
            <div>
              <div className="text-2xl font-bold text-green-600">{assignedCount}</div>
              <div className="text-sm text-gray-600">Scheduled</div>
            </div>
            <div className="w-px bg-gray-300"></div>
            <div>
              <div className="text-2xl font-bold text-orange-600">{unassignedRecipes.length}</div>
              <div className="text-sm text-gray-600">Unscheduled</div>
            </div>
          </div>
        </div>

        {/* Unassigned recipes list */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Unscheduled meals:</h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {unassignedRecipes.map(recipe => (
              <div
                key={recipe.id}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
              >
                <span className="text-lg">
                  {getMealTypeEmoji(recipe.recipe_data.mealType)}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {recipe.recipe_data.name}
                  </div>
                  <div className="text-xs text-gray-500 capitalize">
                    {recipe.recipe_data.mealType}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Info message */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-green-800">
            ✨ These meals will remain available in your calendar to schedule anytime.
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          <button
            onClick={onLeave}
            className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
          >
            Leave Anyway
          </button>
          <button
            onClick={onStay}
            className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Stay & Schedule
          </button>
        </div>
      </div>
    </div>
  )
}

// Helper function to get emoji for meal type
function getMealTypeEmoji(mealType: string | undefined): string {
  const emojis: Record<string, string> = {
    breakfast: '🌅',
    lunch: '☀️',
    dinner: '🌙',
    snacks: '🍎',
    dessert: '🍰'
  }
  return emojis[mealType?.toLowerCase() || 'dinner'] || '🍽️'
}
